import { closeAllPanels } from '@phoenix/helpers/panel-helper.js';
import { Modal, ModalOptions } from '@sg-core/src/View/Components/Organisms/Modal';

import { MENU_STATE_EVENTS, menuState } from './menu-state';

const defaultOptions: ModalOptions = {
  triggerOpenAttribute: 'data-menu-open',
  triggerCloseAttribute: 'data-menu-close',
  overlayClass: 'navigation-menu__overlay',
  containerClass: 'navigation-menu__body',
};

export interface MenuOptions extends ModalOptions {
  activeButtonClass: string;
}

export class Menu extends Modal {
  private activeButtonClass: string;

  constructor(
    el: string | HTMLElement,
    options: MenuOptions = {
      activeButtonClass: 'is-active',
    },
  ) {
    const { activeButtonClass, ...rest } = options;

    super(el, {
      ...defaultOptions,
      ...rest,
    });

    menuState.addInstance(this.rootElement, this);
    menuState.on(MENU_STATE_EVENTS.UPDATE_PATH, this.updateOpenPanels.bind(this));

    this.activeButtonClass = activeButtonClass;
  }

  protected override dispatch(action: string) {
    const path = this.buildPathFrom(this.rootElement as HTMLElement);

    switch (action) {
      case 'show':
        closeAllPanels();
        menuState.setPaths(path);
        break;
      case 'hide': {
        const omittedPath = Array.from(path).slice(0, -1);
        menuState.setPaths(omittedPath);
        break;
      }
      default:
        throw new Error(`Unknown action: ${action}`);
    }

    return Promise.resolve();
  }

  private buildPathFrom(el: HTMLElement) {
    const path: HTMLElement[] = [];
    let cur: HTMLElement | null | undefined = el;

    while (cur) {
      if (cur.classList.contains('panel')) {
        path.unshift(cur);
      }

      cur = cur.parentElement?.closest('.panel');
    }

    return new Set(path);
  }

  private async updateOpenPanels({ oldPaths, newPaths }: { oldPaths: Set<HTMLElement>; newPaths: Set<HTMLElement> }) {
    const hiddingPanels: Promise<void>[] = [];
    const showingPanels: Promise<void>[] = [];

    for (const segment of oldPaths) {
      if (newPaths.has(segment)) {
        continue;
      }

      const panel = menuState.getInstance(segment);

      if (panel) {
        this.getActivablePanelTriggers(panel).forEach((el) => {
          el.classList.remove(this.activeButtonClass);
        });
        hiddingPanels.push(panel.hide());
      }
    }

    await Promise.all(hiddingPanels);

    for (const segment of newPaths) {
      if (oldPaths.has(segment)) {
        continue;
      }

      const panel = menuState.getInstance(segment);

      if (panel) {
        this.getActivablePanelTriggers(panel).forEach((el) => {
          el.classList.add(this.activeButtonClass);
        });
        showingPanels.push(panel.show());
      }
    }

    await Promise.all(showingPanels);

    return Promise.resolve();
  }

  private getActivablePanelTriggers(panel: Menu) {
    return document.querySelectorAll(`button[${panel.options.triggerCloseAttribute}="${panel.rootElement.id}"]`);
  }
}
